/**
 * Document Recognition Pipeline
 * Client-side document classification, field extraction, and prima nota proposal.
 * 
 * Supports: invoice, receipt, bank statement, tax form, contract, payslip
 * Extracts: date, supplier, amount, tax, description, payment method
 * Proposes: prima nota entry with suggested category and cost type
 */

// ─── Types ──────────────────────────────────────────────────────────────────

export type DocumentType = 
  | 'invoice'        // Fattura (passiva/attiva)
  | 'receipt'        // Ricevuta / Scontrino
  | 'bank_statement' // Estratto conto
  | 'tax_form'       // Modello fiscale (F24, CU, 730)
  | 'contract'       // Contratto
  | 'payslip'        // Busta paga / Cedolino
  | 'chart_accounts' // Piano dei conti
  | 'trial_balance'  // Situazione contabile / Bilancio di verifica
  | 'balance_sheet'  // Bilancio / Stato patrimoniale
  | 'other'

export type InvoiceDirection = 'passive' | 'active' // Fattura passiva (costo) vs attiva (ricavo)

export interface ExtractedField {
  label: string
  value: string
  confidence: number // 0-1
}

export interface PrimaNotaProposal {
  type: 'expense' | 'income'
  date: string
  amount: number
  tax?: number
  supplier?: string
  description: string
  suggestedCategory: string
  suggestedCostType: 'fixed' | 'variable'
  categoryIcon?: string
}

export interface RecognitionStep {
  id: string
  label: string
  status: 'pending' | 'running' | 'completed' | 'error'
  detail?: string
  icon?: string
}

export interface DocumentRecognitionResult {
  fileType: string               // csv, pdf, xlsx, xls, image
  documentType: DocumentType
  documentTypeLabel: string
  invoiceDirection?: InvoiceDirection
  confidence: number             // 0-1
  steps: RecognitionStep[]
  extractedFields: ExtractedField[]
  primaNota?: PrimaNotaProposal
  rawTextPreview?: string
}

// ─── Category Mapping ───────────────────────────────────────────────────────

const CATEGORY_MAP: Record<string, { category: string; costType: 'fixed' | 'variable' }> = {
  // Cloud & SaaS
  'aws': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'amazon web services': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'google cloud': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'azure': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'microsoft': { category: 'Software & Licenze', costType: 'fixed' },
  'github': { category: 'Software & Licenze', costType: 'fixed' },
  'gitlab': { category: 'Software & Licenze', costType: 'fixed' },
  'slack': { category: 'Software & Licenze', costType: 'fixed' },
  'notion': { category: 'Software & Licenze', costType: 'fixed' },
  'figma': { category: 'Software & Licenze', costType: 'fixed' },
  'cloudflare': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'vercel': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'heroku': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  'digitalocean': { category: 'Cloud & Infrastruttura', costType: 'fixed' },
  
  // Utilities
  'enel': { category: 'Utenze', costType: 'fixed' },
  'eni': { category: 'Utenze', costType: 'fixed' },
  'a2a': { category: 'Utenze', costType: 'fixed' },
  'edison': { category: 'Utenze', costType: 'fixed' },
  'telecom': { category: 'Telecomunicazioni', costType: 'fixed' },
  'tim': { category: 'Telecomunicazioni', costType: 'fixed' },
  'vodafone': { category: 'Telecomunicazioni', costType: 'fixed' },
  'wind': { category: 'Telecomunicazioni', costType: 'fixed' },
  'fastweb': { category: 'Telecomunicazioni', costType: 'fixed' },
  'iliad': { category: 'Telecomunicazioni', costType: 'fixed' },
  
  // Professional services
  'studio legale': { category: 'Consulenze Legali', costType: 'variable' },
  'avvocato': { category: 'Consulenze Legali', costType: 'variable' },
  'commercialista': { category: 'Consulenza Fiscale', costType: 'fixed' },
  'studio commerciale': { category: 'Consulenza Fiscale', costType: 'fixed' },
  'notaio': { category: 'Spese Notarili', costType: 'variable' },
  
  // Office & property
  'affitto': { category: 'Affitto Ufficio', costType: 'fixed' },
  'condominio': { category: 'Spese Condominiali', costType: 'fixed' },
  'pulizie': { category: 'Servizi Ufficio', costType: 'fixed' },
  
  // Insurance
  'assicurazione': { category: 'Assicurazioni', costType: 'fixed' },
  'generali': { category: 'Assicurazioni', costType: 'fixed' },
  'allianz': { category: 'Assicurazioni', costType: 'fixed' },
  'unipol': { category: 'Assicurazioni', costType: 'fixed' },
  'axa': { category: 'Assicurazioni', costType: 'fixed' },
  
  // Tax & government
  'agenzia entrate': { category: 'Imposte e Tasse', costType: 'fixed' },
  'inps': { category: 'Contributi Previdenziali', costType: 'fixed' },
  'inail': { category: 'Assicurazione Lavoro', costType: 'fixed' },
  'f24': { category: 'Imposte e Tasse', costType: 'fixed' },
}

// ─── Keyword Patterns ────────────────────────────────────────────────────────

const INVOICE_KEYWORDS = [
  'fattura', 'invoice', 'n. fattura', 'nr. fattura', 'numero fattura',
  'fattura n°', 'fattura n.', 'fattura elettronica', 'fattura di vendita',
  'fattura commerciale', 'data fattura', 'totale fattura',
  'codice destinatario', 'codice sdi', 'partita iva', 'p.iva', 'p. iva',
  'codice fiscale', 'c.f.', 'iban', 'bonifico', 'pagamento',
  'imponibile', 'iva', 'aliquota', 'totale dovuto', 'scadenza',
  'ddt', 'documento di trasporto'
]

const RECEIPT_KEYWORDS = [
  'scontrino', 'ricevuta', 'ricevuta fiscale', 'corrispettivo',
  'cassa', 'contanti', 'pos', 'carta di credito', 'bancomat',
  'totale', 'subtotale', 'resto', 'pagato'
]

const BANK_STATEMENT_KEYWORDS = [
  'estratto conto', 'saldo', 'data valuta', 'data contabile',
  'causale', 'dare', 'avere', 'movimento', 'conto corrente',
  'abi', 'cab', 'filiale', 'coordinate bancarie', 'saldo iniziale',
  'saldo finale', 'competenze', 'commissioni', 'interessi'
]

const TAX_FORM_KEYWORDS = [
  'modello f24', 'mod. f24', 'modello 730', 'certificazione unica', 'cu ',
  'dichiarazione', 'agenzia delle entrate', 'tributo', 'codice tributo',
  'periodo di riferimento', 'redditi', 'irpef', 'irap', 'ires',
  'ritenuta', 'ritenuta d\'acconto', 'sezione erario', 'sezione inps',
  'sezione regioni', 'sezione imu', 'delega irrevocabile',
  'modello di pagamento', 'saldo finale euro', 'totale a', 'totale c'
]

// Strong F24 markers — if ≥2 match, it's definitely an F24
const F24_STRONG_MARKERS = [
  'mod. f24', 'modello f24', 'modello di pagamento',
  'delega irrevocabile', 'sezione erario', 'sezione inps',
  'codice tributo', 'agenzia delle entrate'
]

const CONTRACT_KEYWORDS = [
  'contratto', 'accordo', 'convenzione', 'clausola', 'articolo',
  'sottoscritto', 'firmatario', 'decorrenza', 'durata', 'recesso',
  'penale', 'foro competente', 'giurisdizione'
]

const PAYSLIP_KEYWORDS = [
  'cedolino', 'busta paga', 'retribuzione', 'stipendio',
  'mensilità', 'lordo', 'netto', 'contributi', 'tfr',
  'ferie', 'permessi', 'straordinari', 'indennità'
]

const CHART_ACCOUNTS_KEYWORDS = [
  'piano dei conti', 'piano conti', 'chart of accounts',
  'conto contabile', 'codice conto', 'mastro', 'sottoconto',
  'classe', 'gruppo', 'voce contabile', 'conti patrimoniali',
  'conti economici', 'conto dare', 'conto avere',
  'attivo', 'passivo', 'patrimonio netto', 'ricavi', 'costi'
]

const TRIAL_BALANCE_KEYWORDS = [
  'situazione contabile', 'bilancio di verifica', 'trial balance',
  'saldo dare', 'saldo avere', 'totale dare', 'totale avere',
  'progressivo dare', 'progressivo avere', 'saldo iniziale',
  'saldo finale', 'quadratura', 'sbilancio', 'mastrino'
]

const BALANCE_SHEET_KEYWORDS = [
  'stato patrimoniale', 'conto economico', 'bilancio',
  'balance sheet', 'income statement', 'nota integrativa',
  'attività', 'passività', 'capitale sociale', 'riserve',
  'utile', 'perdita', 'risultato', 'esercizio', 'civilistico',
  'riclassificato', 'iv direttiva', 'oic', 'xbrl'
]

// ─── Amount Extraction Patterns ──────────────────────────────────────────────

const AMOUNT_PATTERNS = [
  /(?:totale|total|importo|amount|tot\.?)\s*(?:dovuto|documento|fattura|netto|lordo)?[\s:€]*([0-9]{1,3}(?:[.,][0-9]{3})*[.,][0-9]{2})/i,
  /€\s*([0-9]{1,3}(?:[.,][0-9]{3})*[.,][0-9]{2})/,
  /([0-9]{1,3}(?:\.[0-9]{3})*,[0-9]{2})\s*€/,
  /(?:eur|euro)\s*([0-9]{1,3}(?:[.,][0-9]{3})*[.,][0-9]{2})/i,
]

const TAX_PATTERNS = [
  /(?:iva|vat|imposta)\s*(?:al\s*)?(?:22|10|4|5)?\s*%?\s*[:€]?\s*([0-9]{1,3}(?:[.,][0-9]{3})*[.,][0-9]{2})/i,
  /(?:aliquota|tax)\s*(?:\d+%?)?\s*[:€]?\s*([0-9]{1,3}(?:[.,][0-9]{3})*[.,][0-9]{2})/i,
]

const DATE_PATTERNS = [
  /(?:data|date|del|emissione)[\s:]*(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4})/i,
  /(\d{1,2}[/.-]\d{1,2}[/.-]\d{4})/,
  /(\d{4}[/.-]\d{1,2}[/.-]\d{1,2})/,
]

const PIVA_PATTERN = /(?:p\.?\s*iva|partita\s*iva|vat)\s*[:\s]*([0-9]{11})/i
const CF_PATTERN = /(?:c\.?\s*f\.?|codice\s*fiscale)\s*[:\s]*([A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z])/i

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseAmount(raw: string): number {
  // Handle Italian format: 1.234,56 → 1234.56
  const cleaned = raw.replace(/\s/g, '')
  if (cleaned.includes(',') && cleaned.includes('.')) {
    // 1.234,56 → Italian format
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.'))
  }
  if (cleaned.includes(',') && !cleaned.includes('.')) {
    // 1234,56 → Italian format without thousands separator
    return parseFloat(cleaned.replace(',', '.'))
  }
  return parseFloat(cleaned)
}

function normalizeDate(raw: string): string {
  // Try to return YYYY-MM-DD
  const parts = raw.split(/[/.-]/)
  if (parts.length !== 3) return raw
  
  const [a, b, c_raw] = parts.map(p => parseInt(p, 10))
  
  // YYYY-MM-DD format
  if (a > 1000) return `${a}-${String(b).padStart(2, '0')}-${String(c_raw).padStart(2, '0')}`
  
  // DD/MM/YYYY or DD-MM-YYYY (Italian default)
  if (c_raw > 1000) return `${c_raw}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`
  
  // DD/MM/YY
  if (c_raw < 100) {
    const c = c_raw + (c_raw > 50 ? 1900 : 2000)
    return `${c}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`
  }
  
  return raw
}

function countKeywordMatches(text: string, keywords: string[]): number {
  const lower = text.toLowerCase()
  return keywords.reduce((count, kw) => count + (lower.includes(kw) ? 1 : 0), 0)
}

function findSupplier(text: string): string | undefined {
  const lower = text.toLowerCase()
  
  // Check known suppliers
  for (const key of Object.keys(CATEGORY_MAP)) {
    if (lower.includes(key)) {
      // Return with proper capitalization
      return key.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    }
  }
  
  // Try to extract from "Spett.le" or "Gentile" or "Ragione sociale"
  const supplierPatterns = [
    /(?:spett\.?le|gentile|rag\.?\s*sociale|intestatario|cliente|fornitore)[\s:]*([A-Z][A-Za-z\s&'.]+(?:s\.?r\.?l\.?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?)?)/i,
    /^([A-Z][A-Za-z\s&'.]+(?:S\.?r\.?l\.?|S\.?p\.?A\.?|S\.?n\.?c\.?|S\.?a\.?s\.?))\s*$/m,
  ]
  
  for (const pattern of supplierPatterns) {
    const match = text.match(pattern)
    if (match?.[1]) {
      return match[1].trim().substring(0, 60)
    }
  }
  
  return undefined
}

function findCategory(supplier: string | undefined, text: string): { category: string; costType: 'fixed' | 'variable' } {
  const lower = (supplier || '').toLowerCase() + ' ' + text.toLowerCase()
  
  for (const [key, value] of Object.entries(CATEGORY_MAP)) {
    if (lower.includes(key)) return value
  }
  
  // Default based on common document patterns
  if (lower.includes('consulen')) return { category: 'Consulenze', costType: 'variable' }
  if (lower.includes('manutenzione')) return { category: 'Manutenzione', costType: 'variable' }
  if (lower.includes('materiale') || lower.includes('fornitura')) return { category: 'Materiali e Forniture', costType: 'variable' }
  if (lower.includes('trasporto') || lower.includes('spedizione')) return { category: 'Trasporti e Spedizioni', costType: 'variable' }
  if (lower.includes('formazione') || lower.includes('corso')) return { category: 'Formazione', costType: 'variable' }
  if (lower.includes('marketing') || lower.includes('pubblicità') || lower.includes('advertising')) return { category: 'Marketing', costType: 'variable' }
  
  return { category: 'Altro', costType: 'variable' }
}

// ─── Main Classification Logic ──────────────────────────────────────────────

function classifyFromText(text: string): { type: DocumentType; confidence: number; direction?: InvoiceDirection } {
  // F24 fast-path: strong markers are unambiguous
  const lower = text.toLowerCase()
  const f24Hits = F24_STRONG_MARKERS.filter(m => lower.includes(m)).length
  if (f24Hits >= 2) {
    return { type: 'tax_form', confidence: Math.min(0.95, 0.7 + f24Hits * 0.05) }
  }

  const scores: Record<DocumentType, number> = {
    invoice: countKeywordMatches(text, INVOICE_KEYWORDS),
    receipt: countKeywordMatches(text, RECEIPT_KEYWORDS),
    bank_statement: countKeywordMatches(text, BANK_STATEMENT_KEYWORDS),
    tax_form: countKeywordMatches(text, TAX_FORM_KEYWORDS),
    contract: countKeywordMatches(text, CONTRACT_KEYWORDS),
    payslip: countKeywordMatches(text, PAYSLIP_KEYWORDS),
    chart_accounts: countKeywordMatches(text, CHART_ACCOUNTS_KEYWORDS),
    trial_balance: countKeywordMatches(text, TRIAL_BALANCE_KEYWORDS),
    balance_sheet: countKeywordMatches(text, BALANCE_SHEET_KEYWORDS),
    other: 0
  }
  
  // Find best match
  let bestType: DocumentType = 'other'
  let bestScore = 0
  for (const [type, score] of Object.entries(scores)) {
    if (score > bestScore) {
      bestScore = score
      bestType = type as DocumentType
    }
  }
  
  // Calculate confidence based on how many keywords matched vs total keywords for that type
  const maxPossible: Record<DocumentType, number> = {
    invoice: INVOICE_KEYWORDS.length,
    receipt: RECEIPT_KEYWORDS.length,
    bank_statement: BANK_STATEMENT_KEYWORDS.length,
    tax_form: TAX_FORM_KEYWORDS.length,
    contract: CONTRACT_KEYWORDS.length,
    payslip: PAYSLIP_KEYWORDS.length,
    chart_accounts: CHART_ACCOUNTS_KEYWORDS.length,
    trial_balance: TRIAL_BALANCE_KEYWORDS.length,
    balance_sheet: BALANCE_SHEET_KEYWORDS.length,
    other: 1
  }
  
  const confidence = bestScore === 0 
    ? 0.1 
    : Math.min(0.95, 0.3 + (bestScore / maxPossible[bestType]) * 0.65)
  
  // Determine invoice direction
  let direction: InvoiceDirection | undefined
  if (bestType === 'invoice') {
    const lower = text.toLowerCase()
    if (lower.includes('fattura passiva') || lower.includes('fornitore') || lower.includes('spett.le')) {
      direction = 'passive'
    } else if (lower.includes('fattura attiva') || lower.includes('cliente') || lower.includes('fattura di vendita')) {
      direction = 'active'
    } else {
      // Default to passive (more common in upload context)
      direction = 'passive'
    }
  }
  
  return { type: bestType, confidence, direction }
}

function extractFields(text: string): { date?: string; amount?: number; tax?: number; supplier?: string; piva?: string; cf?: string } {
  let date: string | undefined
  let amount: number | undefined
  let tax: number | undefined
  
  // Extract date
  for (const pattern of DATE_PATTERNS) {
    const match = text.match(pattern)
    if (match?.[1]) {
      date = normalizeDate(match[1])
      break
    }
  }
  
  // Extract amount (take the largest amount found, likely the total)
  const amounts: number[] = []
  for (const pattern of AMOUNT_PATTERNS) {
    const match = text.match(pattern)
    if (match?.[1]) {
      amounts.push(parseAmount(match[1]))
    }
  }
  if (amounts.length > 0) {
    amount = Math.max(...amounts)
  }
  
  // Extract tax
  for (const pattern of TAX_PATTERNS) {
    const match = text.match(pattern)
    if (match?.[1]) {
      tax = parseAmount(match[1])
      break
    }
  }
  
  // Extract supplier
  const supplier = findSupplier(text)
  
  // Extract P.IVA
  const pivaMatch = text.match(PIVA_PATTERN)
  const piva = pivaMatch?.[1]
  
  // Extract CF
  const cfMatch = text.match(CF_PATTERN)
  const cf = cfMatch?.[1]
  
  return { date, amount, tax, supplier, piva, cf }
}

// ─── Document Type Labels ────────────────────────────────────────────────────

const DOC_TYPE_LABELS: Record<string, Record<DocumentType, string>> = {
  it: {
    invoice: 'Fattura',
    receipt: 'Ricevuta / Scontrino',
    bank_statement: 'Estratto Conto',
    tax_form: 'Modello Fiscale',
    contract: 'Contratto',
    payslip: 'Busta Paga',
    chart_accounts: 'Piano dei Conti',
    trial_balance: 'Situazione Contabile',
    balance_sheet: 'Bilancio',
    other: 'Documento generico'
  },
  en: {
    invoice: 'Invoice',
    receipt: 'Receipt',
    bank_statement: 'Bank Statement',
    tax_form: 'Tax Form',
    contract: 'Contract',
    payslip: 'Payslip',
    chart_accounts: 'Chart of Accounts',
    trial_balance: 'Trial Balance',
    balance_sheet: 'Balance Sheet',
    other: 'Generic Document'
  }
}

// ─── File Type Detection ─────────────────────────────────────────────────────

function detectFileType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  const mimeMap: Record<string, string> = {
    csv: 'csv',
    tsv: 'csv',
    xlsx: 'xlsx',
    xls: 'xls',
    pdf: 'pdf',
    xml: 'xml',
    xbrl: 'xml',
    ofx: 'csv',
    qif: 'csv',
    mt940: 'csv',
    txt: 'csv',
    jpg: 'image',
    jpeg: 'image',
    png: 'image',
    tiff: 'image',
    tif: 'image',
    webp: 'image',
    json: 'csv',
  }
  return mimeMap[ext] || 'unknown'
}

// ─── Main Recognition Pipeline ──────────────────────────────────────────────

export async function classifyDocument(
  file: File, 
  pdfText?: string,
  language: string = 'it',
  onStepUpdate?: (steps: RecognitionStep[]) => void
): Promise<DocumentRecognitionResult> {
  
  const fileType = detectFileType(file)
  const steps: RecognitionStep[] = [
    { id: 'detect', label: language === 'it' ? 'Rilevamento tipo file' : 'File type detection', status: 'pending' },
    { id: 'extract', label: language === 'it' ? 'Estrazione testo' : 'Text extraction', status: 'pending' },
    { id: 'classify', label: language === 'it' ? 'Classificazione documento' : 'Document classification', status: 'pending' },
    { id: 'fields', label: language === 'it' ? 'Estrazione campi' : 'Field extraction', status: 'pending' },
    { id: 'proposal', label: language === 'it' ? 'Proposta Prima Nota' : 'Journal entry proposal', status: 'pending' },
  ]
  
  const updateStep = (id: string, status: RecognitionStep['status'], detail?: string) => {
    const step = steps.find(s => s.id === id)
    if (step) {
      step.status = status
      if (detail) step.detail = detail
    }
    onStepUpdate?.([...steps])
  }
  
  // Simulate realistic processing delays
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
  
  // ── Step 1: File type detection ──
  updateStep('detect', 'running')
  await delay(400)
  
  const fileTypeLabel: Record<string, string> = {
    csv: 'CSV (Comma Separated Values)',
    xlsx: 'Microsoft Excel (XLSX)',
    xls: 'Microsoft Excel (XLS)',
    pdf: 'PDF (Portable Document Format)',
    image: language === 'it' ? 'Immagine' : 'Image',
    unknown: language === 'it' ? 'Sconosciuto' : 'Unknown',
  }
  updateStep('detect', 'completed', fileTypeLabel[fileType] || fileType)
  
  // ── Step 2: Text extraction ──
  updateStep('extract', 'running')
  await delay(300)
  
  let textForAnalysis = ''
  
  if (fileType === 'csv' || fileType === 'xlsx' || fileType === 'xls') {
    // Spreadsheets are always bank statements
    updateStep('extract', 'completed', language === 'it' ? 'Dati tabulari rilevati' : 'Tabular data detected')
    
    // For CSV, read first few lines for analysis
    if (fileType === 'csv') {
      try {
        const text = await file.text()
        textForAnalysis = text.substring(0, 2000) // First 2000 chars
      } catch {
        // Ignore
      }
    }
  } else if (fileType === 'pdf') {
    if (pdfText && pdfText.length > 0) {
      textForAnalysis = pdfText
      updateStep('extract', 'completed', `${pdfText.length.toLocaleString()} ${language === 'it' ? 'caratteri estratti' : 'chars extracted'}`)
    } else {
      updateStep('extract', 'completed', language === 'it' ? 'Nessun testo (PDF scansionato?)' : 'No text (scanned PDF?)')
    }
  } else if (fileType === 'image') {
    updateStep('extract', 'completed', language === 'it' ? 'OCR richiesto (non disponibile)' : 'OCR required (not available)')
  } else {
    updateStep('extract', 'completed', language === 'it' ? 'Formato non riconosciuto' : 'Unrecognized format')
  }
  
  // ── Step 3: Classification ──
  updateStep('classify', 'running')
  await delay(500)
  
  let docType: DocumentType = 'other'
  let confidence = 0.5
  let direction: InvoiceDirection | undefined
  
  if (fileType === 'csv' || fileType === 'xlsx' || fileType === 'xls') {
    // Spreadsheets — detect specific type from content
    docType = 'bank_statement'
    confidence = 0.92
    
    if (textForAnalysis) {
      const invoiceScore = countKeywordMatches(textForAnalysis, INVOICE_KEYWORDS)
      const chartScore = countKeywordMatches(textForAnalysis, CHART_ACCOUNTS_KEYWORDS)
      const trialScore = countKeywordMatches(textForAnalysis, TRIAL_BALANCE_KEYWORDS)
      const balanceScore = countKeywordMatches(textForAnalysis, BALANCE_SHEET_KEYWORDS)
      
      const maxScore = Math.max(invoiceScore, chartScore, trialScore, balanceScore)
      if (maxScore > 2) {
        if (maxScore === chartScore) { docType = 'chart_accounts'; confidence = 0.8 }
        else if (maxScore === trialScore) { docType = 'trial_balance'; confidence = 0.8 }
        else if (maxScore === balanceScore) { docType = 'balance_sheet'; confidence = 0.75 }
        else if (maxScore === invoiceScore) { docType = 'invoice'; confidence = 0.7 }
      }
    }
  } else if (textForAnalysis.length > 50) {
    // Classify from text content
    const classification = classifyFromText(textForAnalysis)
    docType = classification.type
    confidence = classification.confidence
    direction = classification.direction
  }
  
  const lang = language === 'it' ? 'it' : 'en'
  const docTypeLabel = DOC_TYPE_LABELS[lang][docType]
  const directionLabel = direction === 'passive' 
    ? (language === 'it' ? ' (passiva)' : ' (payable)') 
    : direction === 'active' 
    ? (language === 'it' ? ' (attiva)' : ' (receivable)') 
    : ''
  
  updateStep('classify', 'completed', `${docTypeLabel}${directionLabel} (${Math.round(confidence * 100)}%)`)
  
  // ── Step 4: Field extraction ──
  updateStep('fields', 'running')
  await delay(400)
  
  const extractedFields: ExtractedField[] = []
  let extractedData: ReturnType<typeof extractFields> = {}
  
  if (textForAnalysis.length > 50) {
    extractedData = extractFields(textForAnalysis)
    
    if (extractedData.date) {
      extractedFields.push({ 
        label: language === 'it' ? 'Data' : 'Date', 
        value: extractedData.date, 
        confidence: 0.85 
      })
    }
    if (extractedData.supplier) {
      extractedFields.push({ 
        label: language === 'it' ? 'Fornitore' : 'Supplier', 
        value: extractedData.supplier, 
        confidence: 0.75 
      })
    }
    if (extractedData.amount) {
      extractedFields.push({ 
        label: language === 'it' ? 'Importo' : 'Amount', 
        value: `€ ${extractedData.amount.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`, 
        confidence: 0.8 
      })
    }
    if (extractedData.tax) {
      extractedFields.push({ 
        label: 'IVA', 
        value: `€ ${extractedData.tax.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`, 
        confidence: 0.7 
      })
    }
    if (extractedData.piva) {
      extractedFields.push({ 
        label: 'P.IVA', 
        value: extractedData.piva, 
        confidence: 0.95 
      })
    }
    if (extractedData.cf) {
      extractedFields.push({ 
        label: language === 'it' ? 'Codice Fiscale' : 'Tax Code', 
        value: extractedData.cf, 
        confidence: 0.95 
      })
    }
  } else if (docType === 'bank_statement') {
    extractedFields.push({
      label: language === 'it' ? 'Tipo' : 'Type',
      value: language === 'it' ? 'Transazioni bancarie' : 'Bank transactions',
      confidence: 0.9
    })
  }
  
  updateStep('fields', 'completed', 
    extractedFields.length > 0 
      ? `${extractedFields.length} ${language === 'it' ? 'campi trovati' : 'fields found'}`
      : (language === 'it' ? 'Nessun campo specifico' : 'No specific fields')
  )
  
  // ── Step 5: Prima nota proposal ──
  updateStep('proposal', 'running')
  await delay(350)
  
  let primaNota: PrimaNotaProposal | undefined
  
  if (docType === 'invoice' || docType === 'receipt') {
    const { category, costType } = findCategory(extractedData.supplier, textForAnalysis)
    
    primaNota = {
      type: direction === 'active' ? 'income' : 'expense',
      date: extractedData.date || new Date().toISOString().split('T')[0],
      amount: extractedData.amount || 0,
      tax: extractedData.tax,
      supplier: extractedData.supplier,
      description: docType === 'invoice' 
        ? `${docTypeLabel}${directionLabel} - ${extractedData.supplier || file.name}`
        : `${docTypeLabel} - ${extractedData.supplier || file.name}`,
      suggestedCategory: category,
      suggestedCostType: costType,
    }
    
    updateStep('proposal', 'completed', 
      language === 'it' 
        ? `Categoria: ${category}` 
        : `Category: ${category}`
    )
  } else if (docType === 'tax_form') {
    primaNota = {
      type: 'expense',
      date: extractedData.date || new Date().toISOString().split('T')[0],
      amount: extractedData.amount || 0,
      description: `${docTypeLabel} - ${file.name}`,
      suggestedCategory: 'Imposte e Tasse',
      suggestedCostType: 'fixed',
    }
    updateStep('proposal', 'completed',
      language === 'it' ? 'Categoria: Imposte e Tasse' : 'Category: Taxes'
    )
  } else if (docType === 'payslip') {
    primaNota = {
      type: 'expense',
      date: extractedData.date || new Date().toISOString().split('T')[0],
      amount: extractedData.amount || 0,
      description: `${docTypeLabel} - ${extractedData.supplier || file.name}`,
      suggestedCategory: 'Costo del Personale',
      suggestedCostType: 'fixed',
    }
    updateStep('proposal', 'completed',
      language === 'it' ? 'Categoria: Costo del Personale' : 'Category: Payroll'
    )
  } else if (docType === 'bank_statement') {
    updateStep('proposal', 'completed', 
      language === 'it' 
        ? 'Elaborazione standard (transazioni multiple)' 
        : 'Standard processing (multiple transactions)'
    )
  } else {
    updateStep('proposal', 'completed', 
      language === 'it' ? 'Non applicabile' : 'Not applicable'
    )
  }
  
  return {
    fileType,
    documentType: docType,
    documentTypeLabel: docTypeLabel + directionLabel,
    invoiceDirection: direction,
    confidence,
    steps,
    extractedFields,
    primaNota,
    rawTextPreview: textForAnalysis ? textForAnalysis.substring(0, 300) : undefined,
  }
}

export { DOC_TYPE_LABELS, CATEGORY_MAP }
